<template>
  <section class="w-full md:px-3"> 
    <ScrollPanel style="width: 100%; height: 450px"
    :pt="{
      bary: 'hover:bg-sea-serpent rounded-md bg-gray-300 opacity-100'
    }"> 
    <form @submit.prevent="updateprofessionalData()" class="grid grid-cols-12 gap-4 text-base font-normal">
      <span class="col-span-12 md:col-span-6">
        <label class="required-sign">{{ translate('name', 'ucfirst') }}</label>
        <CustomInputText name="professional_firstname" class="w-full px-3 py-2 mt-2.5 rounded-full border border-iron" />
      </span>
      <span class="col-span-12 md:col-span-6">
        <label class="required-sign">{{ translate('surname', 'ucfirst') }}</label>
        <CustomInputText name="professional_lastname" class="w-full px-3 py-2 mt-2.5 rounded-full border border-iron" />
      </span>
      <span class="col-span-12 md:col-span-12">
        <label class="required-sign">{{ translate('your title', 'ucfirst') }}</label>
        <CustomTextArea name="professional_title" :max-field-char="150"  class="w-full min-h-28 max-h-36 px-3 py-2 mt-2.5 rounded-md border border-iron" />
      </span>
      <span class="col-span-12 md:col-span-6">
        <label class="required-sign">{{ translate('current country', 'ucfirst') }}</label>
        <CustomDropdown name="dpcountry_id" :options="countryList" optionLabel="lcountry_name" optionValue="lcountry_id"
          filter :loading="fetchingCountries" class="w-full px-3 mt-2.5 capitalize rounded-full border border-iron" />
      </span>
      <span class="col-span-12 md:col-span-6">
        <label class="required-sign">{{ translate('city', 'ucfirst') }}</label>
        <CustomInputText name="dpcity" class="w-full px-3 py-2 mt-2.5 rounded-full border border-iron" />
      </span>
      <span class="col-span-12">
        <Button type="submit" :label="translate('update', 'ucfirst')"
          class="text-white px-5 py-2 float-right rounded-full bg-sea-serpent" />
      </span>
    </form>
    </ScrollPanel>

    <LoadingDialog :visible="isLoading" />
  </section>
</template>

<script setup>
import { ref, onMounted } from "vue"
import Button from "primevue/button"
import { useToast } from "primevue/usetoast";
import { showToast } from "@/composables/toast";
import { translate } from "@/composables/translate";
import requestHandler from "@/composables/requestHandler";
import LoadingDialog from "@/components/Utils/LoadingDialog.vue";
import CustomInputText from "@/components/Base/CustomInputText.vue";
import CustomTextArea from "@/components/Base/CustomTextArea.vue";
import { useForm } from 'vee-validate';
import { toTypedSchema } from "@vee-validate/yup";
import { object, string, number } from 'yup';
import CustomDropdown from "@/components/Base/CustomDropdown.vue";


const props = defineProps({
  dataToUpdate: {
    type: Object,
    required: true,
    default: () => {}
  }
});
const emit = defineEmits(['closeModal', 'dataUpdated'])


let toast = useToast();
let isLoading = ref(false)
let fetchingCountries = ref(false)
let countryList = ref([])

// ############ Validação formulário ############


const { handleSubmit, setValues } = useForm({
  validationSchema: toTypedSchema(
    object({
      professional_firstname: string().required(translate('this field is required', 'ucfirst'))
        .min(2, translate('this field must be at least 2 characters', 'ucfirst')).max(255,
          translate('this field must be at most 255 characters', 'ucfirst')
        ).ensure().trim().default(''),
      professional_lastname: string().required(translate('this field is required', 'ucfirst'))
        .min(2, translate('this field must be at least 2 characters', 'ucfirst')).max(255,
          translate('this field must be at most 255 characters', 'ucfirst')
        ).ensure().trim().default(''),
      professional_title: string().required(translate('this field is required', 'ucfirst'))
      .min(2, translate('this field must be at least 2 characters', 'ucfirst')
      ).max(150,
          translate('this field must be at most 150 characters', 'ucfirst')
      ).ensure().trim().default(''),
      dpcountry_id: number().required(translate('this field is required', 'ucfirst')),
      dpcity: string().required(translate('this field is required', 'ucfirst'))
        .min(2, translate('this field must be at least 2 characters', 'ucfirst')).max(255,
          translate('this field must be at most 255 characters', 'ucfirst')
        ).ensure().trim().default(''),
    })
  )
});


// ############ Criação de Métodos/Funções ############ 


let updateprofessionalData = handleSubmit(async (values) => {

  if (values) {
    isLoading.value = !isLoading.value
    const response = await requestHandler.executeRequest("/api/curriculum/title", values, 'post');
    
    let resPerson = null
    if (props.dataToUpdate.country_id > 0) {
      resPerson = await requestHandler.executeRequest("/api/dataperson", {
        dpcountry_id: values.dpcountry_id,
        dpcity: values.dpcity
      }, 'put');
    }
    else {
      resPerson = await requestHandler.executeRequest("/api/dataperson", {
        dpcountry_id: values.dpcountry_id,
        dpcity: values.dpcity
      }, 'post');
    }

    isLoading.value = !isLoading.value

    if (!response.success) {
      showToast(toast, translate('there was an error updating your profile data', 'ucfirst'), 'warn');
      return
    }
    
    if (!resPerson.success) {
      showToast(toast, translate('there was an error adding your country and city', 'ucfirst'), 'warn');
      return
    }
    showToast(toast, translate('your profile data was updated', 'ucfirst'));
    emit('closeModal', false)
    emit('dataUpdated', true)
  }
})

async function getListCountries() {

  fetchingCountries.value = !fetchingCountries.value
  const response = await requestHandler.executeRequest("/api/countries/index", {}, "get");

  if (response.success) {
    countryList.value = response.data
  }

  fetchingCountries.value = !fetchingCountries.value
}

onMounted(() => {
  getListCountries()

  if (Object.keys(props.dataToUpdate).length > 0) {
    let separate_name = props.dataToUpdate.name_complete.split(' ')
    setValues({
      professional_firstname: separate_name[0],
      professional_lastname: separate_name[1],
      professional_title: props.dataToUpdate.title == null ? '' : props.dataToUpdate.title,
      dpcountry_id: props.dataToUpdate.country_id,
      dpcity: props.dataToUpdate.city
    });
  }
});
</script>